import { Fragment, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Breadcrumbs, Grid, Link, Typography } from "@mui/material";

import { Home as HomeIcon } from "@mui/icons-material";
import { TabPanel } from "~/components/tab-panel";

import { IntegrationSetupDetail } from "~/pages/integrations/IntegrationsSetup/IntegrationSetupDetail/IntegrationSetupDetail";
import appsData from "./utils/integration-setup.json";
import { useCanCreate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Create";
import { ScrollToTop } from "~/lib/scroll-to-top";
import { IntegrationsWorkstation } from "~/pages/integrations/IntegrationsWorkstation/IntegrationsWorkstation";
import { IntegrationSetupAddCiCd } from "~/pages/integrations/IntegrationsSetup/IntegrationSetupDetail/IntegrationSetupAddCiCd";
import { K8OperatorConfiguration } from "~/pages/integrations/kubernetes/K8OperatorConfiguration/K8OperatorConfiguration";
import { AzureIntegrationForm } from "../hosted-integrations/forms/azure/AzureIntegrationForm";
import { AzureBlobIntegrationForm } from "../hosted-integrations/forms/AzureBlobIntegrationForm";
import { GcpIntegrationForm } from "../hosted-integrations/forms/GcpIntegrationForm";
import { Ms365IntegrationForm } from "../hosted-integrations/forms/ms365/Ms365IntegrationForm";
import { OktaIntegrationForm } from "../hosted-integrations/forms/OktaIntegrationForm";
import { GoogleWorkspaceIntegrationForm } from "../hosted-integrations/forms/GoogleWorkspaceIntegrationForm";
import { BigQueryIntegrationForm } from "../hosted-integrations/forms/BigQueryIntegrationForm";
import { SnowflakeIntegrationForm } from "../hosted-integrations/forms/SnowflakeIntegrationForm";
import { S3IntegrationForm } from "../hosted-integrations/forms/S3IntegrationForm";
import { S3CompatibleIntegrationForm } from "../hosted-integrations/forms/S3CompatibleIntegrationForm";
import { GitlabIntegrationForm } from "../hosted-integrations/forms/GitlabIntegrationForm";
import { GithubIntegrationForm } from "../hosted-integrations/forms/GithubIntegrationForm";
import { SlackIntegrationForm } from "../hosted-integrations/forms/SlackIntegrationForm";
import { GcsBucketIntegrationForm } from "../hosted-integrations/forms/GcsBucketIntegrationForm";
import { PostgreSQLIntegrationForm } from "../hosted-integrations/forms/PostgreSQLIntegrationForm";
import { JiraIntegrationForm } from "../hosted-integrations/forms/jira/JiraIntegrationForm/JiraIntegrationForm";
import { OciIntegrationForm } from "../hosted-integrations/forms/OciIntegrationForm";
import { DomainIntegrationForm } from "../hosted-integrations/forms/DomainIntegrationForm";
import { AwsIntegrationForm } from "../hosted-integrations/forms/aws/AwsIntegrationForm";
import { EmailIntegrationForm } from "~/pages/integrations/hosted-integrations/forms/email/EmailIntegrationForm";
import { ZendeskIntegrationForm } from "../hosted-integrations/forms/zendesk/ZendeskIntegrationForm";
import { MsftDefenderIntegrationForm } from "../hosted-integrations/forms/MsftDefenderIntegrationForm";
import { GithubIssuesIntegrationForm } from "../hosted-integrations/forms/github-issues/GithubIssuesIntegrationForm";
import { GitlabIssuesIntegrationForm } from "../hosted-integrations/forms/gitlab-issues/GitlabIssuesIntegrationForm";
import { AzureDevOpsIntegrationForm } from "../hosted-integrations/forms/azure-devops/AzureDevOpsIntegrationForm";
import { ServiceNowIntegrationForm } from "../hosted-integrations/forms/servicenow/ServiceNowIntegrationForm/ServiceNowIntegrationForm";
import { ShodanIntegrationForm } from "../hosted-integrations/forms/ShodanIntegrationForm";
import { WebhookIntegrationForm } from "../hosted-integrations/forms/webhook/WebhookIntegrationForm";
import { SentinelOneIntegrationForm } from "../hosted-integrations/forms/SentinelOneIntegrationForm";
import { CrowdStrikeIntegrationForm } from "../hosted-integrations/forms/CrowdStrikeIntegrationForm";
import { setDocumentTitle } from "~/lib/commonUtils";
import { chatOpsUtils } from "../chatOps/utils/chatOpsUtils";
import { ChatOpsIntegrations } from "../chatOps/ChatOpsIntegrations";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import {
  getAvailableIntegrationAddHref,
  getIntegrationsListHref,
} from "~/pages/integrations/utils/integrationLinksUtils";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { LinuxIntegrationSetupPage } from "../os/linux/LinuxIntegrationSetupPage";
import { CiscoIntegrationSetupPage } from "../cisco/CiscoIntegrationSetupPage";
import { PanosIntegrationSetupPage } from "../panos/PanosIntegrationSetupPage";
import { FortiosIntegrationSetupPage } from "../fortios/FortiosIntegrationSetupPage";
import { JunosIntegrationSetupPage } from "../junos/JunosIntegrationSetupPage";
import { WindowsIntegrationSetupPage } from "../os/windows/WindowsIntegrationSetupPage";
import { MacOSIntegrationSetupPage } from "../os/macos/MacOSIntegrationSetupPage";

export type AppsData = typeof appsData;
export type AppDataKey = keyof AppsData;
export type App = AppsData[AppDataKey];

function isAppDataKey(value: any): value is AppDataKey {
  return Object.keys(appsData).includes(value);
}

export interface IntegrationSetupPageProps {
  scope: SpaceScope | OrganizationScope;
}

export function IntegrationSetupPage({ scope }: IntegrationSetupPageProps) {
  const { appName, integrationTypeId } = useParams();
  const { isFeatureEnabled } = useFeatureFlags();
  const [selectedTab, setSelectedTab] = useState<string>(
    integrationTypeId ? `${appName}/${integrationTypeId}` : "mondoo/setup",
  );
  const label = selectedTab.split("/").pop() || "Setup";
  const integrationLabel = transformIntegrationLabel(selectedTab);

  setDocumentTitle([integrationLabel, "Integrations Setup"]);

  useEffect(() => {
    // Hack to allow us to route from nested integration pages.
    if (integrationTypeId && appName) {
      setSelectedTab(`${appName}/${integrationTypeId}`);
    }
  }, [appName, integrationTypeId]);

  const tabGroups = {
    Workstation: ["mondoo/setup"],
    "Server & Endpoint Security": [
      "mondoo/linux",
      "mondoo/macos",
      "mondoo/windows",
    ],
    "Cloud Security": [
      "mondoo/aws",
      "mondoo/azure",
      "mondoo/gcp",
      "mondoo/kubernetes",
      "mondoo/vmware",
      "mondoo/oci",
    ],
    "Third Party": [
      "mondoo/defender",
      "mondoo/sentinelone",
      "mondoo/crowdstrike",
    ],
    Exports: [
      "mondoo/bigquery",
      "mondoo/gcs_bucket",
      "mondoo/snowflake",
      "mondoo/amazons3",
      "mondoo/amazon-s3-compatible",
      "mondoo/postgres",
      "mondoo/azure_blob",
    ],
    SaaS: [
      "mondoo/ms365",
      "mondoo/okta",
      "mondoo/google_workspace",
      "mondoo/slack",
      "mondoo/github",
      "mondoo/gitlab",
      "mondoo/ticket_system_jira",
      "mondoo/ticket_system_email",
      "mondoo/ticket_system_zendesk",
      "mondoo/ticket_system_github",
      "mondoo/ticket_system_gitlab",
      "mondoo/ticket_system_azure_devops",
      "mondoo/ticket_system_servicenow",
      "mondoo/ticket_system_webhook",
    ],
    "Network Security": [
      "mondoo/shodan",
      "mondoo/cisco",
      "mondoo/panos",
      "mondoo/fortios",
      "mondoo/junos",
      "mondoo/host",
    ],
    "Software Supply Chain": [
      "hashicorp/packer",
      "hashicorp/terraform",
      "mondoo/credentials",
    ],
    "CI/CD": [
      "cicd/gitlab",
      "cicd/github",
      "cicd/kubernetes",
      "cicd/circleci",
      "cicd/azure",
      "cicd/jenkins",
    ],
    "Chat Ops": [
      "chat/msteams",
      "chat/slack",
      "chat/telegram",
      "chat/httppost",
    ],
    "Security Pipeline": ["security_pipeline_github"],
  };

  const hasIntegrationCreatePermission = useCanCreate();
  // If not permitted to create an AWS integration
  if (!hasIntegrationCreatePermission) {
    const cloudTabs = tabGroups["Cloud Security"];
    // Remove AWS from available options
    cloudTabs.splice(cloudTabs.indexOf("mondoo/aws"), 1);
  }

  const renderContentTabSwitch = (selectedTab: string) => {
    let type = selectedTab.split("/").pop();
    const app = isAppDataKey(selectedTab) ? appsData[selectedTab] : null;

    switch (selectedTab) {
      case "mondoo/setup":
        return <IntegrationsWorkstation product="mondoo" />;
      case "mondoo/aws":
      case "mondoo/aws_hosted":
        return <AwsIntegrationForm scope={scope} />;
      case "mondoo/kubernetes":
        return (
          <K8OperatorConfiguration
            {...{ scope }}
            setup={appsData[selectedTab]}
          />
        );
      case "cicd/gitlab":
      case "cicd/github":
      case "cicd/kubernetes":
      case "cicd/circleci":
      case "cicd/azure":
      case "cicd/jenkins":
        return <IntegrationSetupAddCiCd {...{ scope, type: type! }} />;
      case "chat/msteams":
      case "chat/slack":
      case "chat/telegram":
      case "chat/httppost":
        return chatOpsUtils(label) ? (
          <ChatOpsIntegrations {...{ scope, view: label }} />
        ) : (
          <Fragment />
        );

      case "mondoo/windows":
        return <WindowsIntegrationSetupPage />;

      case "mondoo/linux":
        return <LinuxIntegrationSetupPage />;

      case "mondoo/cisco":
        return <CiscoIntegrationSetupPage />;

      case "mondoo/panos":
        return <PanosIntegrationSetupPage />;

      case "mondoo/fortios":
        return <FortiosIntegrationSetupPage />;

      case "mondoo/junos":
        return <JunosIntegrationSetupPage />;

      case "mondoo/macos":
        return <MacOSIntegrationSetupPage />;

      case "mondoo/azure":
        return <AzureIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/azure_blob":
        return <AzureBlobIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/bigquery":
        return <BigQueryIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/gcp":
        return <GcpIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/ms365":
        return <Ms365IntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/okta":
        return <OktaIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/google_workspace":
        return <GoogleWorkspaceIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/snowflake":
        return <SnowflakeIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/amazons3":
        return <S3IntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/amazon-s3-compatible":
        return <S3CompatibleIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/defender":
        return <MsftDefenderIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/sentinelone":
        return <SentinelOneIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/crowdstrike":
        return <CrowdStrikeIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/shodan":
        return <ShodanIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/github":
        return <GithubIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/gitlab":
        return <GitlabIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/slack":
        return <SlackIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/gcs_bucket":
        return <GcsBucketIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/postgres":
        return <PostgreSQLIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/oci":
        return <OciIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/host":
        return <DomainIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/ticket_system_jira":
        return <JiraIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/ticket_system_email":
        return <EmailIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/ticket_system_zendesk":
        return <ZendeskIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/ticket_system_github":
        return <GithubIssuesIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/ticket_system_gitlab":
        return <GitlabIssuesIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/ticket_system_azure_devops":
        return <AzureDevOpsIntegrationForm {...{ scope, type: type! }} />;

      case "mondoo/ticket_system_servicenow":
        if (
          isFeatureEnabled("ServiceNow Integration") &&
          type === "ticket_system_servicenow"
        ) {
          return <ServiceNowIntegrationForm {...{ scope, type: type! }} />;
        }
        break;

      case "mondoo/ticket_system_webhook":
        if (type === "ticket_system_webhook") {
          return <WebhookIntegrationForm {...{ scope }} />;
        }
      // else fallthrough

      default:
        return <IntegrationSetupDetail item={app} scope={scope} />;
    }
  };

  const tabPanels = Object.values(tabGroups)
    .flat()
    .map((id, _index) => (
      <TabPanel value={selectedTab} index={id} key={id}>
        {renderContentTabSwitch(id)}
      </TabPanel>
    ));

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={getIntegrationsListHref({ scope })}
    >
      Integrations
    </Link>,
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={getAvailableIntegrationAddHref({ scope })}
    >
      Add
    </Link>,
    <Typography key={1}>{transformIntegrationLabel(selectedTab)}</Typography>,
  ];

  return (
    <Fragment>
      <ScrollToTop />
      <Box>
        <Breadcrumbs sx={{ mb: 3 }} separator="›">
          {breadcrumbs}
        </Breadcrumbs>
        <Grid container>
          <Grid size="grow">{tabPanels}</Grid>
        </Grid>
      </Box>
    </Fragment>
  );
}

export const stacksUrl = (region: string) => {
  return `https://${region}.console.aws.amazon.com/cloudformation/home?region=${region}#/stacks?filteringText=mondoo`;
};

export const AWS_REGIONS = [
  "ap-northeast-1",
  "ap-northeast-2",
  "ap-northeast-3",
  "ap-south-1",
  "ap-southeast-1",
  "ap-southeast-2",
  "ca-central-1",
  "eu-central-1",
  "eu-north-1",
  "eu-west-1",
  "eu-west-2",
  "eu-west-3",
  "ap-south-1",
  "sa-east-1",
  "us-east-1",
  "us-east-2",
  "us-west-1",
  "us-west-2",
];

// transform url parameter based labels to human friendly labels
const transformIntegrationLabel = (selectedTab: string): string => {
  switch (selectedTab.toLowerCase()) {
    case "chat/httppost":
      return "Outbound Webhook";
    case "cicd/azure":
      return "Azure Pipelines";
    case "mondoo/azure_blob":
      return "Azure Blob Storage";
    case "cicd/circleci":
      return "CircleCI";
    case "cicd/gcp":
      return "GCP";
    case "cicd/github":
      return "GitHub Actions";
    case "cicd/gitlab":
      return "GitLab CI/CD";
    case "mondoo/gitlab":
      return "GitLab";
    case "mondoo/aws":
      return "AWS";
    case "mondoo/linux":
      return "Linux";
    case "mondoo/amazons3":
      return "Amazon S3";
    case "mondoo/amazon-s3-compatible":
      return "S3 Compatible Service";
    case "mondoo/bigquery":
      return "BigQuery";
    case "mondoo/defender":
      return "Microsoft Defender for Cloud";
    case "mondoo/host":
      return "Domain/IP Address";
    case "mondoo/gcp":
      return "Google Cloud Platform";
    case "mondoo/gcs_bucket":
      return "GCP Cloud Storage Bucket";
    case "mondoo/github":
      return "GitHub";
    case "mondoo/ticket_system_jira":
      return "Atlassian Jira";
    case "mondoo/ticket_system_email":
      return "Ticketing via email";
    case "mondoo/ticket_system_zendesk":
      return "Zendesk";
    case "mondoo/ticket_system_github":
      return "GitHub Issues";
    case "mondoo/ticket_system_gitlab":
      return "GitLab Issues";
    case "mondoo/ticket_system_azure_devops":
      return "Azure DevOps";
    case "mondoo/ticket_system_servicenow":
      return "ServiceNow";
    case "mondoo/ticket_system_service_now":
      return "ServiceNow";
    case "mondoo/ticket_system_webhook":
      return "Webhook";
    case "mondoo/macos":
      return "macOS";
    case "mondoo/ms365":
      return "Microsoft 365";
    case "mondoo/okta":
      return "Okta";
    case "mondoo/oci":
      return "Oracle Cloud Infrastructure";
    case "mondoo/google_workspace":
      return "Google Workspace";
    case "chat/msteams":
      return "Microsoft Teams";
    case "mondoo/postgres":
      return "PostgreSQL";
    case "mondoo/setup":
      return "Local Installation";
    case "mondoo/sentinelone":
      return "SentinelOne Singularity";
    case "mondoo/vmware":
      return "VMware";
    case "mondoo/security_pipeline_github":
      return "Github Remediation Workflow";
    case "mondoo/crowdstrike":
      return "CrowdStrike Falcon";
    case "mondoo/panos":
      return "Palo Alto Networks PAN-OS";
    case "mondoo/fortios":
      return "Fortinet FortiOS";
    case "mondoo/junos":
      return "Juniper Networks Junos";
    default:
      const label = selectedTab.split("/").pop() || "";
      return label[0].toUpperCase() + label.slice(1);
  }
};
